import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Warning, CaretDown, CaretRight } from "@phosphor-icons/react";
import { ROLES, INTERNAL_ROLES, CLIENT_ROLES, AFFILIATE_ROLES } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

const GROUPS = [
  { key: "internal", label: "Internal ops", roles: INTERNAL_ROLES,
    hint: "Applies to super_admin, admin, ops, compliance and reporting users." },
  { key: "client", label: "Client users", roles: CLIENT_ROLES,
    hint: "Applies to your paying clients' logins." },
  { key: "affiliate", label: "Affiliate users", roles: AFFILIATE_ROLES,
    hint: "Applies to the traffic sources / partners submitting leads." },
];

/**
 * Pill color for the "N/M enforced" badge on each MFA group card.
 *   • Full → solid black
 *   • Partial → yellow warning
 *   • Empty → muted zinc
 */
function enforcedPillClass(enforcedCount, totalCount) {
  if (enforcedCount === totalCount) return "bg-black text-white border-black";
  if (enforcedCount > 0) return "bg-yellow-200 border-yellow-600";
  return "border-zinc-300 text-zinc-500";
}

/** Super-admin only. Configure which roles must have MFA enabled. */
export default function MfaPolicies() {
  const { user } = useAuth();
  const [rows, setRows] = useState({});
  const [dirty, setDirty] = useState({});
  const [busy, setBusy] = useState(false);
  const [openGroups, setOpenGroups] = useState({ internal: true, client: false, affiliate: false });

  const load = async () => {
    const { data } = await api.get("/mfa/policies");
    setRows(data.policies || {});
    setDirty({});
  };

  useEffect(() => { load(); }, []);

  if (user && user.role !== "super_admin") {
    return (
      <div className="brut-card p-6" data-testid="mfa-policies-denied">
        <div className="label-mono">MFA policies</div>
        <div className="mt-1 font-chivo font-black text-2xl uppercase">Super admin only</div>
        <div className="text-sm text-zinc-600 mt-2">You do not have permission to configure MFA enforcement.</div>
      </div>
    );
  }

  const setField = (role, key, val) => {
    const next = { ...rows[role], [key]: val };
    setRows({ ...rows, [role]: next });
    setDirty({ ...dirty, [role]: true });
  };

  const save = async () => {
    setBusy(true);
    try {
      const patch = Object.keys(dirty).reduce((acc, r) => ({ ...acc, [r]: rows[r] }), {});
      const { data } = await api.patch("/mfa/policies", { policies: patch });
      setRows(data.policies || {});
      setDirty({});
      toast.success("Policies saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const dirtyCount = Object.keys(dirty).length;
  const groupEnforced = (roles) => roles.filter((r) => rows[r]?.required).length;

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="label-mono">Security</div>
          <h1 className="font-chivo font-black text-4xl md:text-5xl uppercase tracking-tighter leading-none">
            MFA enforcement
          </h1>
          <div className="mt-1 text-sm text-zinc-600 max-w-2xl">
            Force TOTP MFA on sign-in for the roles you choose. Existing users get a
            grace window; new users must enroll on first sign-in.
          </div>
        </div>
        <button onClick={save} disabled={busy || dirtyCount === 0}
          data-testid="mfa-policies-save"
          className="bg-black text-white border border-black px-4 py-2 uppercase text-xs tracking-widest font-bold disabled:opacity-60 hover:translate-x-[2px] hover:translate-y-[2px] hover:brut-shadow-sm transition-transform duration-100">
          {(() => {
            if (busy) return "Saving…";
            if (dirtyCount) return `Save (${dirtyCount})`;
            return "Saved";
          })()}
        </button>
      </div>

      <div className="brut-card p-4 mb-4 flex items-start gap-3 bg-yellow-50 border-yellow-500" data-testid="mfa-policies-warn">
        <Warning size={18} className="mt-0.5" weight="bold" />
        <div className="text-xs">
          <b>Heads up:</b> Requiring MFA blocks sign-in for anyone who has NOT enrolled once the grace window
          expires. They will be prompted to enroll before the session is issued.
        </div>
      </div>

      <div className="space-y-3">
        {GROUPS.map((g) => {
          const enforced = groupEnforced(g.roles);
          const isOpen = openGroups[g.key];
          return (
            <div key={g.key} className="brut-card" data-testid={`mfa-group-${g.key}`}>
              <button type="button"
                onClick={() => setOpenGroups({ ...openGroups, [g.key]: !isOpen })}
                data-testid={`mfa-group-toggle-${g.key}`}
                className="w-full flex items-center justify-between px-4 py-3 border-b border-black hover:bg-yellow-50">
                <div className="flex items-center gap-3">
                  {isOpen ? <CaretDown size={16} weight="bold" /> : <CaretRight size={16} weight="bold" />}
                  <div className="text-left">
                    <div className="font-chivo font-black uppercase tracking-tight text-lg">{g.label}</div>
                    <div className="text-[11px] text-zinc-600">{g.hint}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${enforcedPillClass(enforced, g.roles.length)}`}>
                    {enforced}/{g.roles.length} enforced
                  </span>
                </div>
              </button>
              {isOpen && (
                <div className="overflow-x-auto">
                <table className="w-full text-sm font-mono min-w-[640px]">
                  <thead>
                    <tr className="border-b border-zinc-200 text-[10px] uppercase tracking-widest">
                      <th className="py-2 px-4 text-left">Role</th>
                      <th className="py-2 px-4 text-left">Enforce MFA</th>
                      <th className="py-2 px-4 text-left">Grace days</th>
                      <th className="py-2 px-4 text-left">Effective</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.roles.map((r) => {
                      const row = rows[r] || {};
                      return (
                        <tr key={r} className={`border-b border-zinc-100 last:border-b-0 ${dirty[r] ? "bg-yellow-50" : ""}`}
                            data-testid={`mfa-pol-${r}`}>
                          <td className="py-2 px-4">
                            <div className="font-semibold">{ROLES[r] || r}</div>
                            <div className="text-[10px] text-zinc-500">{r}</div>
                          </td>
                          <td className="py-2 px-4">
                            <label className="inline-flex items-center gap-2 cursor-pointer">
                              <input type="checkbox" data-testid={`mfa-pol-req-${r}`}
                                checked={!!row.required}
                                onChange={(e) => setField(r, "required", e.target.checked)}
                                className="border-2 border-black h-4 w-4" />
                              <span className="text-[11px] uppercase tracking-widest">
                                {row.required ? "Required" : "Optional"}
                              </span>
                            </label>
                          </td>
                          <td className="py-2 px-4">
                            <input type="number" min={0} max={90}
                              data-testid={`mfa-pol-grace-${r}`}
                              className="w-20 border border-black px-2 py-1 text-center"
                              value={row.grace_days ?? 0}
                              onChange={(e) => setField(r, "grace_days", parseInt(e.target.value || "0", 10))} />
                            <span className="text-[10px] text-zinc-500 ml-2">days (0–90)</span>
                          </td>
                          <td className="py-2 px-4 text-[11px]">
                            {row.required ? (
                              <span className="inline-flex items-center gap-1 bg-black text-white px-2 py-1">
                                <ShieldCheck size={10} weight="fill" /> ENFORCED
                              </span>
                            ) : (
                              <span className="text-zinc-500">Voluntary</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
